import React, { useEffect, useState, useMemo } from 'react';
import apiService from '../../API/ApiService';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import './UnderConstruction.css';

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const exportToExcel = (data, filename) => {
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "DS5 Data");
  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const file = new Blob([excelBuffer], { type: "application/octet-stream" });
  saveAs(file, `${filename}.xlsx`);
};

export default function Dashboard() {
  const [movementCounts, setMovementCounts] = useState([]);
  const [movementColors, setMovementColors] = useState({});
  const [waitingCounts, setWaitingCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [startdate, setStartDate] = useState('');
  const [enddate, setEndDate] = useState('');

  // Couleurs fixes pour chaque movement
  const colorMap = useMemo(() => ({
    'Quality Control': '#FF8C00',
    'Grading and Quality Control': '#FFA500',
    'Packaging': '#9370DB',
    'Charging': '#AA46BE',
    'Repair': '#DC143C',
    'Repair Joystick': '#ff5347ff',
    'Full Test': '#FFD700',
    'Cleaning and Polishing': '#00CED1',
    'Assemblage DS5': '#2E8B57',
    'BER': '#8B0000',
    'Export': '#4169E1',
    'Repair Batterie / Button' : '#FF4500',
  }), []);

// Ordre correct et définitif des mouvements
  const orderedMovements = [
    "Quick Drift",
    "Quick Reprise",
    "Charging",
    "Full Test",
    "Cleaning and Polishing",
    "Grading and Quality Control",
    "Packaging",
    "Quality Control",
    "Export",
    "Repair",
    "Repair Joystick",
    "Repair Batterie / Button",
    "Assemblage DS5"
  ];
  const exportMovementData = async (current, next, nextName) => {
    try {
      const userId = localStorage.getItem('userId');

      // Récupérer les données depuis la même API que fetchDS5History
      const res = await apiService.get("/getMovementCadence", {
        params: { userId, startdate, enddate }
      });

      const list = res.data.data || [];

      // Filtrer uniquement les items pour ce current movement et next movement
      const filteredList = list.filter(item => {
        let cur = item.current_movement || '';
        const nxt = item.next_movement || '';

        // Cas spécial Quick Drift → Quick Reprise
        if (cur === 'Quick Drift' && (nxt === 'Full Test' || nxt === 'Packaging')) {
          cur = 'Quick Reprise';
        }

        return cur === current && nxt === next;
      });

      if (filteredList.length === 0) {
        alert("Aucune donnée trouvée pour ce mouvement.");
        return;
      }

      // Colonnes à exporter
      const columnsToExport = [
        "serial_number",
        "current_movement",
        "next_movement",
        "operator",
        "tester",
        "date",
        "Panne",
        "Panne_QuickDrift",
        "Grade",
        "model_QuickDrift"
      ];

      const filteredData = filteredList.map(item => {
        const filteredItem = {};
        columnsToExport.forEach(col => {
          filteredItem[col] = item[col] ?? "";
        });
        return filteredItem;
      });

      const startDateStr = startdate.split('T')[0];
      const endDateStr = enddate.split('T')[0];
      const filename = `${current}_to_${nextName}_${startDateStr}_to_${endDateStr}`;

      exportToExcel(filteredData, filename);

    } catch (e) {
      console.error('Erreur export Excel:', e);
    }
  };
  // Tous les mouvements suivants pour BarChart
  const allNextMovements = useMemo(() => {
    const set = new Set();
    movementCounts.forEach(entry => {
      Object.keys(entry).forEach(k => {
        if(k !== 'current_movement') set.add(k);
      });
    });
    // Tri des next movements selon l'ordre
    return Array.from(set).sort((a, b) => {
      const indexA = orderedMovements.indexOf(a);
      const indexB = orderedMovements.indexOf(b);
      if(indexA === -1 && indexB === -1) return 0;
      if(indexA === -1) return 1;
      if(indexB === -1) return -1;
      return indexA - indexB;
    });
  }, [movementCounts]);

  // Transformation des données pour le chart
  const transformMovementData = (rawData) => {
    const grouped = {};
    rawData.forEach(item => {
      let current = item.current_movement || 'N/A';
      const next = item.next_movement || 'N/A';
      
      // 🔹 Condition spéciale pour Quick Drift
      if (current === 'Quick Drift' && (next === 'Full Test' || next === 'Packaging')) {
        current = 'Quick Reprise';
      }

      grouped[current] = grouped[current] || {};
      
      if (!grouped[current][next]) {
        grouped[current][next] = 0; // juste le count
      }

      grouped[current][next] += 1;
    });

    // Transformation en array pour chart et mini cards
    const result = Object.keys(grouped).map(current => ({
      current_movement: current,
      ...grouped[current]
    }));

    // 🔹 Tri selon l'ordre défini (incluant Quick Reprise)
    return result.sort((a, b) => {
      const indexA = orderedMovements.indexOf(a.current_movement);
      const indexB = orderedMovements.indexOf(b.current_movement);
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  };

  const fetchDS5History = async (params = {}) => {
    setLoading(true);
    try {
       const userId = localStorage.getItem('userId');
       console.log("userId in fetchDS5History is ", userId);
      const apiParams = { ...params, userId };
      const res = await apiService.get("/getMovementCadence", { params: apiParams });
      const data = res.data.data || [];

      console.log("DS5 history data is ", data);
      console.log("apiParams in fetchDS5History is ", apiParams);

      const transformed = transformMovementData(data);

      // Tri des current movements selon l'ordre
        const sorted = transformed.sort((a, b) => {
        const indexA = orderedMovements.indexOf(a.current_movement);
        const indexB = orderedMovements.indexOf(b.current_movement);
        if(indexA === -1 && indexB === -1) return 0;
        if(indexA === -1) return 1;
        if(indexB === -1) return -1;
        return indexA - indexB;
      });

      setMovementCounts(sorted);
      setMovementColors(colorMap);
    } catch (err) {
      console.error('Error fetching DS5 history:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchWaitingCounts = async () => {
    try {
      const res = await apiService.get("/getDataDS5_sn");
      const list = res.data || [];
      console.log("Full DS5 SN data for waiting counts:", list);
      const counts = {};
      orderedMovements.forEach(movement => {
        counts[movement] = list.filter(item => item.next_movement === movement).length;
      });
      
      setWaitingCounts(counts);
    } catch (err) {
      console.error('Error fetching waiting counts:', err);
    }
  };


  useEffect(() => {
    const today = new Date();
    const todayStr = today.toISOString().slice(0, 16); 
    setStartDate(todayStr);
    setEndDate(todayStr);

    // Envoi startDate et endDate séparés
    fetchDS5History({ startdate: todayStr, enddate: todayStr });
      fetchWaitingCounts();
  }, [colorMap]);
  
  const handleSearch = () => {
    const params = {};
    if (startdate) params.startdate = startdate;
    if (enddate) params.enddate = enddate;
    fetchDS5History(params); // Appel API sans reload de la page
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <h5>Loading data...</h5>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div style={{ background: '#1a3c6e', color: 'white', padding: '20px', textAlign: 'center' }}>
        <h1 style={{ margin: 0, fontSize: '2.5em', fontWeight: 500 }}>🎮 Cadence & Waiting</h1>
        <p style={{color:"white"}}>DS5 Movement Tracking and Daily Statistics</p>
      </div>
      {/* Filtre de date */}
      <div className="mb-3 d-flex justify-content-center align-items-end gap-3">
        <div style={{ margin: '15px 0 0', fontSize: '1.1em' }}>
          <label>Start Date:</label>
          <input type="datetime-local" value={startdate} onChange={e => setStartDate(e.target.value)} />
        </div>
        <div style={{ margin: '15px 0 0', fontSize: '1.1em' }}>
          <label>End Date:</label>
          <input type="datetime-local" value={enddate} onChange={e => setEndDate(e.target.value)} />
        </div>
        <button className="btn btn-primary" onClick={handleSearch}>Search</button>
      </div>

      {/* Bar Chart */}
      <div className="row g-3">
        <div className="col-lg-12 col-md-12">
          <div className="card shadow-sm p-5 h-100">
            <h5 className="fw-bold mb-5">DS5 Controllers per Movement</h5>
            {movementCounts.length === 0 ? (
              <div className="text-center py-5"><h5>No data available for this date range.</h5></div>
            ) : (
              <ResponsiveContainer width="100%" height={500}>
                <BarChart data={movementCounts} margin={{ top: 10, right: 10, left: 0, bottom: 80 }} barCategoryGap="30%">
                  <XAxis dataKey="current_movement" tick={{ fontSize: 12 }} angle={-40} textAnchor="end" />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value, name) => [`${value}`, name]} />
                  <Legend verticalAlign="top" height={25} />
                  {allNextMovements.map(next => (
                    <Bar key={next} dataKey={next} stackId="a" fill={movementColors[next]} label={({ value }) => (value > 0 ? value : '')} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
              
            )}
           
          </div>
        </div>

        {/* Mini cards */}
   <div className="col-12 mt-4">
  <div className="row g-3">
    {movementCounts.length === 0 ? (
      <div className="text-center py-5"><h5>No data available for this date range.</h5></div>
    ) : (
      movementCounts.map(entry => {
        const current = entry.current_movement;

        // Tri nextMovements selon l'ordre
        const nextMovements = Object.keys(entry)
          .filter(k => k !== 'current_movement')
          .sort((a, b) => {
            const indexA = orderedMovements.indexOf(a);
            const indexB = orderedMovements.indexOf(b);
            if(indexA === -1 && indexB === -1) return 0;
            if(indexA === -1) return 1;
            if(indexB === -1) return -1;
            return indexA - indexB;
          });

        const total = nextMovements.reduce((sum, next) => sum + entry[next], 0);
        const colorHeader = '#247db9ff';

        return (
          <div key={current} className="col-12">
            <div className="card shadow-sm h-100">
              {/* Card Header */}
              <div className="p-3 fw-bold text-white" style={{ backgroundColor: colorHeader, borderTopLeftRadius: 8, borderTopRightRadius: 8, fontSize: '1.2rem' }}>
                {current} <span className="float-end">Total: {total}</span>
              </div>

              {/* Mini Table Header */}
              <div className="d-flex justify-content-between text-center mb-1 mt-1" style={{ fontWeight: 600 }}>
                <div style={{ flex: 1, margin: '0 2px', padding: '7px 5px', borderRadius: 6, backgroundColor: '#91c3e4ff', fontWeight: 'bold', fontSize: '1.1rem', color: 'black' }}>Next Movement</div>
                <div style={{ flex: 1, margin: '0 2px', padding: '7px 5px', borderRadius: 6, backgroundColor: '#91c3e4ff', fontWeight: 'bold', fontSize: '1.1rem', color: 'black' }}>Quantity</div>
              </div>

              <div className="p-3">
             {nextMovements.map(next => {
              const data = entry[next]; // {count, id_movement, id_nextjob}
              const isAlert = next === 'Repair' || next === 'BER' || next === 'Repair Joystick' || next === 'Repair Batterie / Button';
              return (
                <div key={next} className="d-flex justify-content-between text-center mb-2">
                  <div style={{
                    flex: 1, margin: '0 2px', padding: '7px 5px', borderRadius: 6,
                    fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f1eeee'
                  }}>
                    {next}
                    {/* BOUTON EXCEL */}
                   
                    <button
                  onClick={() => exportMovementData(entry.current_movement, next, next)}              
                        className="btn btn-success btn-sm">
                      Excel
                    </button>
                  </div>

                  <div style={{
                    flex: 1, margin: '0 2px', padding: '7px 5px', borderRadius: 6,
                    fontWeight: 'bold', fontSize: '1.3rem', backgroundColor: '#e0e0e0',
                    color: isAlert ? 'red' : colorHeader
                                  }}>
                  {entry[next]} {/* <-- afficher seulement le nombre */}
                      </div>
                    </div>
                  )
                })}

              </div>
            </div>
          </div>
        );
      })
    )}
  </div>
</div>

{/* Tableau Waiting */}
<div className="col-12 mt-4">
  <div className="card shadow-sm p-4">
    <h5 className="fw-bold mb-3">Waiting per Movement (all data)</h5>
    <table className="table table-striped table-hover text-center align-middle">
      <thead className="table-primary">
        <tr>
          <th>Movement</th>
          <th>Waiting Count</th>
          <th>Export</th>
        </tr>
      </thead>
      <tbody>
        {orderedMovements.map(movement => {
          const count = waitingCounts[movement] ?? 0;

          // Export Excel pour ce movement
          const exportWaitingExcel = async () => {
            try {
              const res = await apiService.get("/getDataDS5_sn");
              const list = res.data || [];

              // Filtrer les lignes où next_movement correspond
              const filteredList = list.filter(item => item.next_movement === movement);

              if (!filteredList.length) {
                alert("Aucune donnée trouvée pour ce mouvement.");
                return;
              }

              const columnsToExport = [
                "serial_number",
                "current_movement",
                "next_movement",
                "date",
                "Grade",
                "Quick_Drift_panne",
                "panne"
              ];

              const filteredData = filteredList.map(item => {
                const row = {};
                columnsToExport.forEach(col => row[col] = item[col] ?? "");
                return row;
              });

              exportToExcel(filteredData, `Waiting_${movement}`);
            } catch (err) {
              console.error("Erreur export Excel:", err);
            }
          };

          return (
            <tr key={movement} className={count === 0 ? 'table-light' : ''}>
              <td className="fw-bold">{movement}</td>
              <td>{count}</td>
              <td>
                {count > 0 && (
                  <button 
                    onClick={exportWaitingExcel}
                    className="btn btn-success btn-sm"
                  >
                    Export Excel
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
</div>

      </div>
    </div>
  );
}
